import React, {useCallback, useEffect, useMemo, useState} from "react"
import dayjs from "dayjs"
import "dayjs/locale/es"
import DeleteIcon from "@mui/icons-material/Delete"
import PersonIcon from "@mui/icons-material/Person"
import StarIcon from "@mui/icons-material/Star"
import StarHalfIcon from "@mui/icons-material/StarHalf"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import PageSection from "@/layouts/PageSection"
import {
   deleteReviewProperty,
   getReviewsProperties,
   PropertyReview,
   PropertyReviewsResponse,
} from "@/api/properties"

type PropertyReviewsSectionProps = {
   propertyId: string
   canManageReviews: boolean
}

const parseTimestamp = (value: unknown): dayjs.Dayjs | null => {
   if (!value) return null
   if (typeof value === "string") {
      const parsed = dayjs(value)
      return parsed.isValid() ? parsed : null
   }
   if (value instanceof Date) {
      return dayjs(value)
   }
   const timestamp = value as {seconds?: number; _seconds?: number; toDate?: () => Date}
   const seconds = timestamp.seconds ?? timestamp._seconds
   if (typeof seconds === "number") {
      return dayjs.unix(seconds)
   }
   if (typeof timestamp.toDate === "function") {
      return dayjs(timestamp.toDate())
   }
   return null
}

const formatReviewDate = (review: PropertyReview) => {
   const parsed = parseTimestamp(review.updatedAt) ?? parseTimestamp(review.createdAt)
   if (!parsed) return "Sin fecha"
   return parsed.locale("es").format("DD/MMM/YYYY").replace(".", "")
}

const isValidRating = (rating: number) =>
   Number.isFinite(rating) && rating >= 0.5 && rating <= 5 && Number.isInteger(rating * 2)

const StarRating = ({value, size = "medium"}: {value: number; size?: "small" | "medium"}) => {
   const textSize = size === "small" ? "text-[24px]" : "text-[38px]"

   return (
      <div className={`flex items-center leading-none text-yellow-400 ${textSize}`}>
         {[1, 2, 3, 4, 5].map((starIndex) => {
            const className = "drop-shadow-[0_1px_0_#c2410c]"
            if (value >= starIndex) {
               return <StarIcon key={starIndex} fontSize="inherit" className={className} />
            }
            if (value >= starIndex - 0.5) {
               return <StarHalfIcon key={starIndex} fontSize="inherit" className={className} />
            }
            return <StarBorderIcon key={starIndex} fontSize="inherit" className={className} />
         })}
      </div>
   )
}

const normalizeReviews = (response: PropertyReviewsResponse | null) => {
   if (!response?.reviews) return []
   return response.reviews
      .map((review) => ({
         ...review,
         rating: Number(review.rating) || 0,
      }))
      .sort((a, b) => {
         const dateA = parseTimestamp(a.updatedAt) ?? parseTimestamp(a.createdAt)
         const dateB = parseTimestamp(b.updatedAt) ?? parseTimestamp(b.createdAt)
         return (dateB?.unix() ?? 0) - (dateA?.unix() ?? 0)
      })
}

export default function PropertyReviewsSection({
   propertyId,
   canManageReviews,
}: PropertyReviewsSectionProps) {
   const [reviewsResponse, setReviewsResponse] = useState<PropertyReviewsResponse | null>(null)
   const [loading, setLoading] = useState(false)
   const [savingReviewId, setSavingReviewId] = useState<string | null>(null)
   const [error, setError] = useState("")

   const fetchReviews = useCallback(async () => {
      if (!propertyId) return
      setLoading(true)
      setError("")
      try {
         const response = await getReviewsProperties(propertyId)
         setReviewsResponse(response)
      } catch (fetchError) {
         console.error("Error cargando calificaciones", fetchError)
         setError("No se pudieron cargar las calificaciones.")
      } finally {
         setLoading(false)
      }
   }, [propertyId])

   useEffect(() => {
      fetchReviews()
   }, [fetchReviews])

   const reviews = useMemo(() => normalizeReviews(reviewsResponse), [reviewsResponse])
   const validReviewCount = reviews.filter((review) => isValidRating(review.rating)).length
   const averageRating = reviewsResponse?.averageRating ?? 0
   const reviewCount = reviewsResponse?.reviewCount ?? validReviewCount
   const displayRating = reviewCount === 0 ? 5 : averageRating

   const handleDelete = async (reviewId: string) => {
      if (!canManageReviews || savingReviewId) return
      setSavingReviewId(reviewId)
      setError("")
      try {
         await deleteReviewProperty(propertyId, reviewId)
         await fetchReviews()
      } catch (deleteError) {
         console.error("Error eliminando calificación", deleteError)
         setError("No se pudo eliminar la calificación.")
      } finally {
         setSavingReviewId(null)
      }
   }

   return (
      <PageSection id="property-reviews" title="Calificaciones">
         <div className="mx-auto flex max-w-[980px] flex-col items-center gap-8">
            <div className="flex flex-wrap items-center justify-center gap-2 text-xl font-semibold text-gray-500">
               <span>Rating General</span>
               <span>{displayRating.toFixed(1)}</span>
               <StarRating value={displayRating} />
               <span className="text-blue">({reviewCount})</span>
            </div>

            <div className="h-px w-52 bg-gray-400" />

            {error && <p className="text-center text-sm text-red-600">{error}</p>}
            {loading && <p className="text-center text-gray-500">Cargando calificaciones...</p>}
            {!loading && reviews.length === 0 && (
               <p className="text-center text-gray-500">No hay calificaciones registradas</p>
            )}

            <div className="flex w-full flex-col gap-6">
               {reviews.map((review) => (
                  <article
                     key={review.id}
                     className="grid gap-4 border-b border-gray-300 pb-6 last:border-b-0 md:grid-cols-[240px_1fr] md:items-start"
                  >
                     <div className="flex flex-col items-center gap-2 text-center">
                        <div className="flex items-center gap-3">
                           <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-gray-600 bg-[#c7f2b5]">
                              {review.userPhotoURL ? (
                                 <img
                                    src={review.userPhotoURL}
                                    alt={review.userName || review.userEmail}
                                    className="h-full w-full object-cover"
                                 />
                              ) : (
                                 <PersonIcon className="text-gray-600" />
                              )}
                           </div>
                           <p className="text-lg text-gray-700">
                              {review.userName || review.userEmail || "Usuario"}
                           </p>
                        </div>
                        <div className="flex items-center gap-1">
                           <StarRating value={review.rating} size="small" />
                           <span className="text-blue">({review.rating})</span>
                        </div>
                        <p className="text-gray-700">{formatReviewDate(review)}</p>
                     </div>

                     <div className="relative min-h-[96px] rounded-2xl border-2 border-gray-300 px-5 py-4 text-lg text-gray-500">
                        <p className="whitespace-pre-wrap pr-8">
                           {review.comment || "Sin comentarios"}
                        </p>
                        {canManageReviews && (
                           <button
                              type="button"
                              className="absolute bottom-3 right-3 text-orange-600 disabled:text-orange-300"
                              aria-label="Eliminar calificación"
                              disabled={savingReviewId === review.id}
                              onClick={() => handleDelete(review.id)}
                           >
                              <DeleteIcon />
                           </button>
                        )}
                     </div>
                  </article>
               ))}
            </div>
         </div>
      </PageSection>
   )
}
